import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Checkbox,
  FormControlLabel,
  Divider,
  Stack,
  Tooltip,
  Chip,
} from "@mui/material";
import CustomSearch from "../../Components/CustomSearch";
import CustomTab from "../../Components/CustomTab";
import PreviewComponent from "../mockTest/QuestionFormComponentMock/previewComponent";
import { FontFamily } from "../contentFeedback/Preview/getAlphabetForIndex";
import { Delete } from "@mui/icons-material";
import CustomIconButton from "../../Components/CustomIconButton";
import ClusterConfigDialog from "./ClusterConfigDialog";
import apiRequest from "../../request/apiRequest/apiRequest";
import { allApis } from "../../request/config";
import { fetchAllMastersLocal } from "../../store/slices/detailedMaster/detailedMasterSlice";
import SvgPreview from "../../Components/SvgPreview";
import { cross } from "../../assets/svgs";
import { subHeading } from "../../Components/CustomTexts";
import { displayRow, rowHorizontalEnd, rowSB } from "../../GlobalStyles";
import CustomContainedButton from "../../Components/CustomContainedButton";
import GlobalLoader from "../../Components/GlobalLoader";
import { showSnack } from "../../store/slices/constants";
import { useAppDispatch } from "../../hooks/storeHooks";
import useCopiedData from "../../store/slices/constants/staticContents/useCopiedData";

interface DistributeQuestionsDialogProps {
  open: boolean;
  onClose: () => void;
  onPaste?: (response: any) => void;
  hierarchy?: any;
  setLoader: any;
}

const DistributeQuestionsDialog: React.FC<DistributeQuestionsDialogProps> = ({
  open,
  onClose,
  onPaste,
  hierarchy,
  setLoader,
}) => {
  const dispatch = useAppDispatch();
  const { copiedData, setCopiedData } = useCopiedData();
  const [subTopics, setSubtopics] = useState<any[]>([]);
  useEffect(() => {
    try {
      const stored = localStorage.getItem("copied");
      if (stored) {
        const decoded = JSON.parse(decodeURIComponent(escape(atob(stored))));
        if (Array.isArray(decoded) && decoded.length > 0)
          setCopiedData(decoded);
      }
    } catch (err) {
      console.warn("Error reading copied data:", err);
    }
  }, []);
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const [assigned, setAssigned] = useState<Record<string, string[]>>({});
  const [activeSubtopic, setActiveSubtopic] = useState<string | null>(null);
  const [filteredSubtopics, setFilteredSubtopics] = useState(subTopics || []);
  const [selectedTabId, setSelectedTabId] = useState<number>(0);
  const [clusterDialogOpen, setClusterDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    if (hierarchy && hierarchy.topic_id && open) {
      const FETCHSub = async () => {
        const payload: any = {
          level: "subtopic",
          parent_id: hierarchy.topic_id,
        };

        const res: any = await fetchAllMastersLocal(payload);
        setSubtopics(res.subtopics);
      };
      FETCHSub();
    }
  }, [hierarchy, open]);
  useEffect(() => {
    const init: Record<string, string[]> = {};
    if (subTopics.length && subTopics.length > 0) {
      subTopics.forEach((s: any) => (init[s.id] = []));

      setAssigned(init);
      setSelectedQuestions([]);
      setActiveSubtopic(null);
      setFilteredSubtopics(subTopics || []);
      setSelectedTabId(0);
    } else {
    }
  }, [subTopics, copiedData]);
  const handleChildDelete = (parentIdx: number, idToRemove: any) => {
    const updated = copiedData.map((item, idx) => {
      if (idx !== parentIdx) return item;

      // 🔹 If it's a parent-type item
      if (item.parent && Array.isArray(item.childs)) {
        return {
          ...item,
          childs: item.childs.filter((child: any) => child.id !== idToRemove),
        };
      }

      // 🔹 If it's a question-type item
      if (Array.isArray(item.questions)) {
        return {
          ...item,
          questions: item.questions.filter((q: any) => q.id !== idToRemove),
        };
      }

      return item;
    });

    setCopiedData(updated);

    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(updated))));
    localStorage.setItem("copied", encoded);
  };
  const groupedQuestions = useMemo(
    () =>
      (copiedData || []).map((c) => ({
        ...c,
        heading: `${c.series?.label || "Untitled"} > ${
          c.mocktest?.label || "N/A"
        } > ${c.section?.label || "N/A"}`,
        questions:
          (c.questions || []).map((q: any, idx: number) => ({
            ...q,
            displayIndex: idx + 1,
            _groupHeading: `${c.series?.label || "Untitled"} > ${
              c.mocktest?.label || "N/A"
            } > ${c.section?.label || "N/A"}`,
          })) || [],
      })),
    [copiedData]
  );

  const allQuestions = useMemo(
    () =>
      groupedQuestions.flatMap((g) => g.questions.map((q: any) => ({ ...q }))),
    [groupedQuestions]
  );

  const isAssigned = (qid: string) =>
    Object.values(assigned || {}).some((arr) => arr && arr.includes(qid));

  const pendingQuestions = useMemo(
    () => allQuestions.filter((q: any) => !isAssigned(q.id)),
    [allQuestions, assigned]
  );
  const assignedQuestions = useMemo(
    () => allQuestions.filter((q: any) => isAssigned(q.id)),
    [allQuestions, assigned]
  );

  const toggleQuestion = (id: string) => {
    setSelectedQuestions((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const assignToSubtopic = (subtopicId: string) => {
    if (!selectedQuestions.length) return;

    setAssigned((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        next[k] = (next[k] || []).filter(
          (qid) => !selectedQuestions.includes(qid)
        );
      });
      next[subtopicId] = Array.from(
        new Set([...(next[subtopicId] || []), ...selectedQuestions])
      );
      return next;
    });

    setActiveSubtopic(subtopicId);
    setSelectedQuestions([]);
  };

  const moveToPending = (qid: string) => {
    setAssigned((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        next[key] = (next[key] || []).filter((id) => id !== qid);
      });
      return next;
    });
  };

  const allAssigned =
    allQuestions.length === 0
      ? false
      : allQuestions.every((q) => isAssigned(q.id));

  // 🔹 On Submit → Open Cluster Config Dialog
  const handleOpenClusterDialog = () => {
    setClusterDialogOpen(true);
  };

  console.log(copiedData, "onSubmit");
  // 🔹 Final API Submit
  const handleClusterSubmit = async (form: any) => {
    setLoader(true);
    setIsSubmitting(true);
    const dataPayload = Object.entries(assigned)
      .filter(([_, qids]) => qids.length > 0)
      .map(([subtopic_id, question_ids]) => ({
        subtopic_id: Number(subtopic_id),
        question_ids,
      }));

    const payload = form.separate_cluster
      ? {
          separate_cluster: form.separate_cluster,
          cluster_type: form.cluster_type,
          max_time: form.max_time,
          min_time: form.min_time,
          data: dataPayload,
        }
      : {
          separate_cluster: form.separate_cluster,
          data: dataPayload,
          cluster_type: form.cluster_type,
        };

    try {
      const res = await apiRequest({
        method: "POST",
        api: allApis.mock_move,
        payload,
      });

      if (res.error) {
        setLoader(false);
        dispatch(
          showSnack({
            open: true,
            severity: "error",
            message: res.msg,
          })
        );
      } else {
        console.log(copiedData, "onSubmit");
        if (onPaste) onPaste(res);
        onClose();
      }
    } catch (error) {
      console.error("Error submitting clusters:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- UI Sections ---
  const pendingContent = (
    <Box p="2px">
      {groupedQuestions.map((group, gIndex) => (
        <Box key={`g-${gIndex}`} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {group.heading}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {group.questions
            .filter((q: { id: string }) => !isAssigned(q.id))
            .map((q: any) => (
              <Paper
                key={q.id}
                elevation={1}
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "flex-start",
                  p: 1.25,
                  mb: 1,
                  borderRadius: 1.5,
                  backgroundColor: selectedQuestions.includes(q.id)
                    ? "#e8f4ff"
                    : "#fff",
                  cusror: "pointer",
                }}
                onClick={() => {
                  toggleQuestion(q.id);
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox checked={selectedQuestions.includes(q.id)} />
                    }
                    label={`Q${q.displayIndex}`}
                    sx={{ m: 0 }}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography
                    component="div"
                    color="#121212"
                    fontWeight={400}
                    fontSize="15px"
                    lineHeight="1.4"
                    fontFamily={FontFamily.gilroy}
                  >
                    <PreviewComponent htmlContent={q.text} />
                    <Tooltip key={q.id} title="Remove this item">
                      <Chip
                        label={`#${q.id}`}
                        size="small"
                        variant="outlined"
                        onDelete={() => {
                          const parentIndex = groupedQuestions.findIndex(
                            (grp) =>
                              grp.questions?.some(
                                (ques: any) => ques.id === q.id
                              )
                          );
                          if (parentIndex !== -1) {
                            handleChildDelete(parentIndex, q.id);
                          }
                        }} // 👈 new action
                        deleteIcon={
                          <Delete
                            sx={{
                              fontSize: "0.9rem",
                            }}
                          />
                        }
                        sx={{
                          cursor: "pointer",
                          fontSize: "0.75rem",
                          border: "none",
                          padding: "0 2px",
                        }}
                      />
                    </Tooltip>
                  </Typography>
                </Box>
              </Paper>
            ))}
        </Box>
      ))}

      {selectedQuestions.length > 0 && (
        <Typography
          variant="body2"
          color="primary"
          sx={{ mt: 1, fontStyle: "italic" }}
        >
          Now you have to select one subtopic to assign selected questions.
        </Typography>
      )}
    </Box>
  );

  const assignedContent = (
    <Box p="2px">
      {groupedQuestions.map((group, gIndex) => {
        const groupAssigned = group.questions.filter((q: any) =>
          isAssigned(q.id)
        );
        if (!groupAssigned.length) return null;
        return (
          <Box key={`ag-${gIndex}`} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              {group.heading}
            </Typography>
            <Divider sx={{ mb: 1 }} />
            {groupAssigned.map((q: any) => {
              const assignedToEntry = Object.entries(assigned).find(([, arr]) =>
                arr.includes(q.id)
              );
              const assignedToId = assignedToEntry?.[0] ?? null;
              const assignedToName = subTopics?.find(
                (s: any) => Number(s.id) === Number(assignedToId)
              )?.name;

              return (
                <Paper
                  key={`assigned-${q.id}`}
                  elevation={0}
                  sx={{
                    p: 1.25,
                    mb: 1,
                    borderRadius: 1.5,
                    border: "1px solid #eee",
                    backgroundColor: "#fbfbfb",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Box
                      sx={{
                        width: 56,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>
                        Q{q.displayIndex}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        component="div"
                        sx={{ fontSize: "15px", lineHeight: 1.4 }}
                      >
                        <PreviewComponent htmlContent={q.text} />
                      </Typography>
                      <Box
                        sx={{
                          mt: 0.75,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "green", display: "block" }}
                        >
                          Assigned to: {assignedToName}
                        </Typography>

                        <CustomIconButton
                          content={<Delete />}
                          onClick={() => moveToPending(q.id)}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );

  const allTab = [
    {
      id: 0,
      tab: `Pending (${pendingQuestions.length})`,
      content: pendingContent,
    },
    {
      id: 1,
      tab: `Assigned (${assignedQuestions.length})`,
      content: assignedContent,
    },
  ];

  const renderSubtopicCard = (s: any) => {
    const assignedQuestionIds = assigned[s.id] || [];
    return (
      <Paper
        key={s.id}
        elevation={0}
        sx={{
          border: "1px solid #e6e6e6",
          borderRadius: 2,
          p: 1.25,
          mb: 1,
          cursor: selectedQuestions.length > 0 ? "pointer" : "default",
          backgroundColor: activeSubtopic === s.id ? "#e6f7ff" : "#fff",
          transition: "background-color 150ms ease",
          "&:hover": {
            backgroundColor:
              selectedQuestions.length > 0 ? "#dff4ff" : "#f9f9f9",
          },
        }}
        onClick={() => assignToSubtopic(s.id)}
      >
        <Typography sx={{ fontWeight: 700 }}>{s.name}</Typography>
        {assignedQuestionIds.length > 0 && (
          <Box sx={{ mt: 0.75 }}>
            {assignedQuestionIds.map((qid: string) => {
              const q = allQuestions.find((x) => x.id === qid);
              return (
                <Typography
                  key={qid}
                  variant="caption"
                  display="block"
                  sx={{ mb: 0.25 }}
                >
                  • Q{q?.displayIndex} #{q?.id}
                </Typography>
              );
            })}
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        onContextMenu={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <DialogTitle
          sx={{ m: 0, p: "20px 15px 15px", ...rowSB }}
          id="customized-dialog-title"
        >
          {subHeading({
            text: "Distribute Selected Questions to Subtopics",
            size: "1rem",
            weight: "500",
          })}
          <CustomIconButton
            onClick={onClose}
            noHover={true}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
            content={<SvgPreview svg={cross} />}
          />
        </DialogTitle>

        <DialogContent dividers>
          {isSubmitting ? <GlobalLoader open={true} /> : ""}
          <Stack sx={{ height: "100%", ...displayRow, width: "100%" }}>
            <Box width="calc(50% - 5px)" height="100%" mr="10px">
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  boxShadow: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  boxSizing: "border-box",
                }}
              >
                {/* <CustomSearch
                  placeholder="Search question by text, ID or Q number"
                  copyData={allQuestions}
                  fieldName={["text","id","displayIndex"]}
                  filteredData={handleQuestionSearch}
                  onClear={() => setFilteredQuestions(allQuestions)}
                /> */}

                <CustomTab
                  selected={allTab.find((it) => it.id === selectedTabId)}
                  tabs={allTab}
                  onChange={(id: number) => setSelectedTabId(id)}
                  badgeCount={{}}
                />
              </Paper>
            </Box>

            <Box width="calc(50% - 5px)" height="100%">
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  boxShadow: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  boxSizing: "border-box",
                }}
              >
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                  Subtopics of {hierarchy ? hierarchy.topic ?? "" : ""}
                </Typography>

                <CustomSearch
                  copyData={subTopics || []}
                  fieldName={["name"]}
                  filteredData={(arr: any[]) => setFilteredSubtopics(arr)}
                  onClear={() => setFilteredSubtopics(subTopics || [])}
                />

                <Box
                  className="scrollBluePrimarythin"
                  sx={{ mt: 1, flex: 8, overflowY: "auto" }}
                >
                  {(filteredSubtopics || []).length ? (
                    filteredSubtopics.map((s: any) => renderSubtopicCard(s))
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      No subtopics found
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ m: 0, p: "10px", ...rowHorizontalEnd }}>
          <CustomIconButton onClick={onClose} content="Cancel" />
          <CustomContainedButton
            content={isSubmitting ? "Submitting..." : "Proceed"}
            otherProps={{
              disabled: !allAssigned || isSubmitting,
              onClick: handleOpenClusterDialog,
            }}
          />
        </DialogActions>
      </Dialog>

      {/* 🔹 Cluster Config Modal */}
      <ClusterConfigDialog
        open={clusterDialogOpen}
        onClose={() => setClusterDialogOpen(false)}
        onConfirm={handleClusterSubmit}
      />
    </>
  );
};

export default DistributeQuestionsDialog;
